import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReverification, useUser } from '@clerk/clerk-react';
import { isReverificationCancelledError } from '@clerk/clerk-react/errors';
import { CameraIcon, LinkIcon } from '@heroicons/react/24/outline';
import AccountReverificationModal, {
  type ReverificationPurpose,
} from '../components/auth/AccountReverificationModal';
import Breadcrumb from '../components/layout/Breadcrumb';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Dropdown from '../components/ui/Dropdown';
import Input from '../components/ui/Input';
import VerificationCodeInput from '../components/ui/VerificationCodeInput';
import ToastContainer from '../components/ui/ToastContainer';
import { useAccessLevel } from '../hooks/useAccessLevel';
import { useHospitalOhpDomains } from '../hooks/useHospitalOhpDomains';
import { usePageTitle } from '../hooks/usePageTitle';
import { useToast } from '../hooks/useToast';
import { getAccessLevelDisplayName, getEmailDomain } from '../utils/accessLevel';
import { ORBCON_ADMIN_DOMAIN } from '../types/access';
import {
  NAME_PREFIX_OPTIONS,
  PROFESSION_OPTIONS,
  PROFILE_CLINICAL_AREAS,
  formatUserDisplayName,
  hospitalsMatchingDomains,
  isPlaceholderLastName,
  readProfileExtras,
  emptyProfileExtras,
  type UserProfileExtras,
} from '../types/userProfile';
import { clinicalAreaLabels } from '../utils/metadataHelpers';
import SelectableChips from '../components/ui/SelectableChips';
import TopicInterestPicker from '../components/ui/TopicInterestPicker';
import AccountPreferences from '../components/profile/AccountPreferences';

function clerkErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'errors' in error) {
    const errors = (error as { errors?: Array<{ longMessage?: string; message?: string }> }).errors;
    const first = errors?.[0];
    const message = first?.longMessage || first?.message;
    if (message && /taken|already exists|already been taken/i.test(message)) {
      return 'That email is already on another ORBCoN account. Sign in with that account, or remove the email there first.';
    }
    if (message) return message;
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Something went wrong. Please try again.';
}

function connectedAccountLabel(provider: string): string {
  if (provider.includes('google')) return 'Google';
  if (provider.includes('microsoft')) return 'Microsoft';
  if (provider.includes('linkedin')) return 'LinkedIn';
  return provider.replace(/^oauth_/, '').replace(/_/g, ' ');
}

function isEmailVerified(status: string | null | undefined): boolean {
  return status === 'verified';
}

function emailTypeAndStatus(options: {
  isPrimary: boolean;
  hospitalMatch: boolean;
  adminDomain: boolean;
  verified: boolean;
}): string {
  const types = [
    options.isPrimary ? 'Primary' : null,
    options.hospitalMatch ? 'Hospital domain' : null,
    options.adminDomain ? 'Admin domain' : null,
  ].filter(Boolean);
  return [...types, options.verified ? 'Verified' : 'Unverified'].join(' · ');
}

export default function Profile() {
  const { user, isLoaded } = useUser();
  const [reverification, setReverification] = useState<{
    level?: string;
    purpose: ReverificationPurpose;
    onComplete: () => void;
    onCancel: () => void;
  } | null>(null);
  const reverificationPurposeRef = useRef<ReverificationPurpose>('add-work-email');
  const onNeedsReverification = useCallback(
    ({
      complete,
      cancel,
      level,
    }: {
      complete: () => void;
      cancel: () => void;
      level?: string;
    }) => {
      setReverification({
        level,
        purpose: reverificationPurposeRef.current,
        onComplete: () => {
          complete();
          setReverification(null);
        },
        onCancel: () => {
          cancel();
          setReverification(null);
        },
      });
    },
    []
  );
  const createEmailAddress = useReverification((email: string) => {
    if (!user) {
      throw new Error('You need to be signed in.');
    }
    return user.createEmailAddress({ email });
  }, { onNeedsReverification });
  const destroyEmailAddress = useReverification(async (emailId: string) => {
    const address = user?.emailAddresses.find((item) => item.id === emailId);
    if (!address) {
      throw new Error('That email is no longer on this account.');
    }
    await address.destroy();
  }, { onNeedsReverification });
  const updatePrimaryEmail = useReverification(async (emailId: string) => {
    if (!user) {
      throw new Error('You need to be signed in.');
    }
    await user.update({ primaryEmailAddressId: emailId });
  }, { onNeedsReverification });
  const { accessLevel, email } = useAccessLevel();
  const { domains: hospitalDomains, sites, isLoading: sitesLoading } = useHospitalOhpDomains();
  const { toasts, success, error: showError, removeToast } = useToast();
  usePageTitle('Profile');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [extras, setExtras] = useState<UserProfileExtras>(emptyProfileExtras);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [newWorkEmail, setNewWorkEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingEmailId, setPendingEmailId] = useState<string | null>(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [addEmailOpen, setAddEmailOpen] = useState(false);
  const [accountConfirmed, setAccountConfirmed] = useState(false);

  useEffect(() => {
    if (!user) return;
    setFirstName(user.firstName || '');
    setLastName(isPlaceholderLastName(user.lastName) ? '' : user.lastName || '');
    setExtras(readProfileExtras(user.unsafeMetadata));
  }, [user]);

  useEffect(() => {
    if (!isLoaded || !user || window.location.hash !== '#preferences') return;
    document.getElementById('preferences')?.scrollIntoView();
  }, [isLoaded, user]);

  const emailEntries = user?.emailAddresses ?? [];
  const hasUnverifiedEmail = emailEntries.some(
    (address) => !isEmailVerified(address.verification?.status)
  );
  const verifiedDomains = emailEntries
    .filter((address) => isEmailVerified(address.verification?.status))
    .map((address) => getEmailDomain(address.emailAddress))
    .filter((domain): domain is string => Boolean(domain));
  const matchingHospitals = useMemo(
    () => hospitalsMatchingDomains(sites, verifiedDomains),
    [sites, verifiedDomains]
  );
  const hasHospitalEmail = verifiedDomains.some((domain) => hospitalDomains.includes(domain));
  const isAdminEmail = verifiedDomains.includes(ORBCON_ADMIN_DOMAIN);
  const showWorkEmailPrompt = !hasHospitalEmail && !isAdminEmail;
  const hospitalOptions = matchingHospitals.map((site) => ({ value: site.id, label: site.name }));
  const selectedOrganizationIds = (extras.organizationIds || []).filter((id) =>
    matchingHospitals.some((site) => site.id === id)
  );

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    if (!user) return;
    setSaving(true);
    try {
      const previous = { ...(user.unsafeMetadata as Record<string, unknown>) };
      delete previous.region;
      delete previous.organization;
      await user.update({
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
        unsafeMetadata: {
          ...previous,
          namePrefix: extras.namePrefix || '',
          jobTitle: extras.jobTitle?.trim() || '',
          profession: extras.profession || '',
          organizationIds: selectedOrganizationIds,
          clinicalAreas: extras.clinicalAreas || [],
          topics: extras.topics || [],
        },
      });
      success('Profile saved');
    } catch (err) {
      showError(clerkErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function handlePhotoChange(file: File | null) {
    if (!user) return;
    setUploadingPhoto(true);
    try {
      await user.setProfileImage({ file });
      success(file ? 'Photo updated' : 'Photo removed');
    } catch (err) {
      showError(clerkErrorMessage(err));
    } finally {
      setUploadingPhoto(false);
    }
  }

  async function sendVerificationCode(emailId: string) {
    if (!user) return;
    const address = user.emailAddresses.find((item) => item.id === emailId);
    if (!address) {
      setEmailError('That email is no longer on this account.');
      return;
    }
    setEmailBusy(true);
    setEmailError(null);
    try {
      await address.prepareVerification({ strategy: 'email_code' });
      setPendingEmailId(address.id);
      setVerificationCode('');
      success(`Verification code sent to ${address.emailAddress}`);
    } catch (err) {
      setEmailError(clerkErrorMessage(err));
    } finally {
      setEmailBusy(false);
    }
  }

  async function handleAddWorkEmail() {
    if (!user) return;
    setEmailBusy(true);
    setEmailError(null);
    reverificationPurposeRef.current = 'add-work-email';
    try {
      const created = await createEmailAddress(newWorkEmail.trim());
      if (!created) {
        throw new Error('Could not add that email.');
      }
      await created.prepareVerification({ strategy: 'email_code' });
      setPendingEmailId(created.id);
      setAddEmailOpen(false);
      setAccountConfirmed(false);
      success(`Verification code sent to ${newWorkEmail.trim()}`);
    } catch (err) {
      if (isReverificationCancelledError(err)) {
        return;
      }
      setEmailError(clerkErrorMessage(err));
    } finally {
      setEmailBusy(false);
    }
  }

  function closeAddEmailForm() {
    setAddEmailOpen(false);
    setAccountConfirmed(false);
    setNewWorkEmail('');
    setEmailError(null);
  }

  function handleConfirmAccount() {
    setEmailError(null);
    reverificationPurposeRef.current = 'add-work-email';
    setReverification({
      level: 'first_factor',
      purpose: 'add-work-email',
      onComplete: () => {
        setReverification(null);
        setAccountConfirmed(true);
      },
      onCancel: () => setReverification(null),
    });
  }

  async function handleVerifyWorkEmail(code = verificationCode) {
    if (!user || !pendingEmailId) return;
    const address = user.emailAddresses.find((item) => item.id === pendingEmailId);
    if (!address) {
      setEmailError('That email is no longer on this account.');
      return;
    }
    const trimmed = code.trim();
    if (trimmed.length !== 6) return;
    setEmailBusy(true);
    setEmailError(null);
    try {
      await address.attemptVerification({ code: trimmed });
      await user.reload();
      setPendingEmailId(null);
      setVerificationCode('');
      setNewWorkEmail('');
      success('Work email verified');
    } catch (err) {
      setEmailError(clerkErrorMessage(err));
    } finally {
      setEmailBusy(false);
    }
  }

  async function handleRemoveEmail(emailId: string) {
    if (!user) return;
    const address = user.emailAddresses.find((item) => item.id === emailId);
    if (!address) return;
    setEmailBusy(true);
    setEmailError(null);
    reverificationPurposeRef.current = 'remove-email';
    try {
      await destroyEmailAddress(emailId);
      await user.reload();
      if (pendingEmailId === emailId) {
        setPendingEmailId(null);
        setVerificationCode('');
      }
      success('Email removed');
    } catch (err) {
      if (isReverificationCancelledError(err)) {
        return;
      }
      setEmailError(clerkErrorMessage(err));
    } finally {
      setEmailBusy(false);
    }
  }

  async function handleMakePrimary(emailId: string) {
    if (!user) return;
    setEmailBusy(true);
    setEmailError(null);
    reverificationPurposeRef.current = 'make-primary';
    try {
      await updatePrimaryEmail(emailId);
      await user.reload();
      success('Primary email updated');
    } catch (err) {
      if (isReverificationCancelledError(err)) {
        return;
      }
      setEmailError(clerkErrorMessage(err));
    } finally {
      setEmailBusy(false);
    }
  }

  if (!isLoaded || !user) {
    return null;
  }

  const accounts = user.externalAccounts ?? [];
  const pendingAddress = emailEntries.find((address) => address.id === pendingEmailId);
  const primaryEmailId = user.primaryEmailAddressId;
  const listedEmails = [...emailEntries].sort((a, b) => {
    if (a.id === primaryEmailId) return -1;
    if (b.id === primaryEmailId) return 1;
    return 0;
  });

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <ToastContainer toasts={toasts} onClose={removeToast} />
      <Breadcrumb
        items={[
          { label: 'Home', to: '/' },
          { label: 'Profile' },
        ]}
      />

      <h1 className="text-3xl font-bold text-gray-900 mb-2">Profile</h1>
      <p className="text-gray-600 mb-8">
        These details personalize My ORBCoN. Access still comes from your verified email domains.
      </p>

      <form onSubmit={handleSave} className="space-y-6">
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Photo and name</h2>
          <div className="flex items-start gap-6 mb-6">
            {user.imageUrl ? (
              <img src={user.imageUrl} alt="" className="h-20 w-20 rounded-full object-cover" />
            ) : (
              <span className="h-20 w-20 rounded-full bg-primary/10 text-primary text-xl font-semibold flex items-center justify-center">
                {(firstName[0] || email?.[0] || '?').toUpperCase()}
              </span>
            )}
            <div className="space-y-2">
              <label className="inline-flex items-center gap-2 min-h-[44px] px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer focus-within:ring-2 focus-within:ring-primary">
                <CameraIcon className="h-5 w-5" aria-hidden />
                {uploadingPhoto ? 'Uploading…' : 'Change photo'}
                <input
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  disabled={uploadingPhoto}
                  onChange={(event) => {
                    const file = event.target.files?.[0] ?? null;
                    if (file) void handlePhotoChange(file);
                    event.target.value = '';
                  }}
                />
              </label>
              {user.hasImage && (
                <button
                  type="button"
                  className="block text-sm text-gray-600 hover:text-primary min-h-[44px] focus-ring rounded-sm"
                  disabled={uploadingPhoto}
                  onClick={() => void handlePhotoChange(null)}
                >
                  Remove photo
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[9.5rem_minmax(0,1fr)_minmax(0,1fr)] gap-4">
            <Dropdown
              label="Prefix"
              options={[...NAME_PREFIX_OPTIONS]}
              value={extras.namePrefix || ''}
              placeholder="No prefix"
              onChange={(value) => setExtras((current) => ({ ...current, namePrefix: value }))}
            />
            <Input
              label="First name"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              autoComplete="given-name"
            />
            <Input
              label="Last name"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              autoComplete="family-name"
            />
          </div>
          <p className="text-sm text-gray-500 mt-3">
            Displayed as {formatUserDisplayName({
              prefix: extras.namePrefix,
              firstName,
              lastName,
              email,
            })}
          </p>
        </Card>

        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Work details</h2>
          <div className="space-y-4">
            <Input
              label="Job title"
              value={extras.jobTitle || ''}
              onChange={(event) => setExtras((current) => ({ ...current, jobTitle: event.target.value }))}
              autoComplete="organization-title"
              placeholder="Transfusion safety officer"
            />
            <Dropdown
              label="Profession"
              options={PROFESSION_OPTIONS}
              value={extras.profession || ''}
              placeholder="Select a profession"
              onChange={(value) => setExtras((current) => ({ ...current, profession: value }))}
            />

            <div>
              <p className="block text-sm font-medium text-gray-700 mb-2">Work emails</p>
              <ul className="space-y-2 mb-3">
                {listedEmails.map((address) => {
                  const verified = isEmailVerified(address.verification?.status);
                  const domain = getEmailDomain(address.emailAddress);
                  const hospitalMatch = Boolean(domain && hospitalDomains.includes(domain));
                  const isPrimary = address.id === primaryEmailId;
                  return (
                    <li key={address.id} className="flex items-start justify-between gap-3 text-sm">
                      <div>
                        <p className="text-gray-900">{address.emailAddress}</p>
                        <p className="text-gray-500">
                          {emailTypeAndStatus({
                            isPrimary,
                            hospitalMatch,
                            adminDomain: domain === ORBCON_ADMIN_DOMAIN,
                            verified,
                          })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {!verified && (
                          <button
                            type="button"
                            className="text-sm font-medium text-primary hover:text-primary-dark min-h-[44px] focus-ring rounded-sm px-1"
                            disabled={emailBusy}
                            onClick={() => void sendVerificationCode(address.id)}
                          >
                            {pendingEmailId === address.id ? 'Resend code' : 'Verify'}
                          </button>
                        )}
                        {!isPrimary && verified && (
                          <button
                            type="button"
                            className="text-sm font-medium text-primary hover:text-primary-dark min-h-[44px] focus-ring rounded-sm px-1"
                            disabled={emailBusy}
                            onClick={() => void handleMakePrimary(address.id)}
                          >
                            Make primary
                          </button>
                        )}
                        {!isPrimary && (
                          <button
                            type="button"
                            className="text-sm text-gray-600 hover:text-primary min-h-[44px] focus-ring rounded-sm px-1"
                            disabled={emailBusy}
                            onClick={() => void handleRemoveEmail(address.id)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>

              {hasUnverifiedEmail && (
                <p className="text-sm text-gray-600 mb-3">
                  Verify an existing email before adding another. Accounts created before this flow
                  often still need that step.
                </p>
              )}

              {showWorkEmailPrompt && (
                <p className="text-sm text-gray-600 mb-3">
                  Your current email isn’t on our hospital domain list. Add and verify an Ontario
                  healthcare professional email to unlock resources and features for Ontario hospital
                  professionals, including resources specific to your site.
                </p>
              )}

              {pendingAddress ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Enter the code we sent to {pendingAddress.emailAddress}.
                  </p>
                  <VerificationCodeInput
                    value={verificationCode}
                    onChange={setVerificationCode}
                    onComplete={(code) => void handleVerifyWorkEmail(code)}
                    error={emailError ?? undefined}
                    disabled={emailBusy}
                  />
                  <div className="flex flex-wrap gap-3">
                    <Button
                      type="button"
                      variant="tertiary"
                      className="min-h-[44px]"
                      disabled={emailBusy}
                      onClick={() => {
                        setPendingEmailId(null);
                        setVerificationCode('');
                        setEmailError(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : addEmailOpen && accountConfirmed ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Account confirmed. Send a code to the work email you want to add.
                  </p>
                  <Input
                    label="Work email"
                    type="email"
                    value={newWorkEmail}
                    onChange={(event) => setNewWorkEmail(event.target.value)}
                    autoComplete="email"
                    error={emailError ?? undefined}
                  />
                  <div className="flex flex-wrap gap-3">
                    <Button
                      type="button"
                      variant="secondary"
                      className="min-h-[44px]"
                      disabled={emailBusy || !newWorkEmail.trim()}
                      onClick={() => void handleAddWorkEmail()}
                    >
                      {emailBusy ? 'Sending…' : 'Send verification code'}
                    </Button>
                    <Button
                      type="button"
                      variant="tertiary"
                      className="min-h-[44px]"
                      disabled={emailBusy}
                      onClick={closeAddEmailForm}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : addEmailOpen ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    You can sign in with any verified work email on your account. Adding one is a
                    sensitive change. Confirm it’s you first, then we’ll send a code to your primary
                    email. After that, you can send a code to verify and add the new work address.
                  </p>
                  {emailError && (
                    <p className="text-sm text-error" role="alert">
                      {emailError}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-3">
                    <Button
                      type="button"
                      variant="secondary"
                      className="min-h-[44px]"
                      disabled={emailBusy || Boolean(reverification)}
                      onClick={handleConfirmAccount}
                    >
                      Confirm it’s you
                    </Button>
                    <Button
                      type="button"
                      variant="tertiary"
                      className="min-h-[44px]"
                      disabled={emailBusy}
                      onClick={closeAddEmailForm}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  className="min-h-[44px]"
                  onClick={() => {
                    setEmailError(null);
                    setAddEmailOpen(true);
                  }}
                >
                  Add a work email
                </Button>
              )}
            </div>

            <Dropdown
              label="Organizations or hospitals"
              multiple
              options={hospitalOptions}
              value={selectedOrganizationIds}
              placeholder="Select hospitals"
              emptyMessage={
                sitesLoading
                  ? 'Loading hospitals…'
                  : 'Verify a hospital work email to choose organizations'
              }
              disabled={hospitalOptions.length === 0}
              onChange={(value) => setExtras((current) => ({ ...current, organizationIds: value }))}
            />
            {hospitalOptions.length > 1 && (
              <p className="text-sm text-gray-500">
                Add every hospital or network you work with. Shared domains can list more than one site.
              </p>
            )}
          </div>
        </Card>

        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Interests</h2>
          <p className="text-sm text-gray-600 mb-4">
            Clinical areas and topics you want to see more of.
          </p>
          <div className="space-y-6">
            <SelectableChips
              label="Clinical areas"
              hint="Select every area that applies."
              options={PROFILE_CLINICAL_AREAS.map((value) => ({
                value,
                label: clinicalAreaLabels[value],
              }))}
              value={extras.clinicalAreas || []}
              onChange={(value) =>
                setExtras((current) => ({
                  ...current,
                  clinicalAreas: PROFILE_CLINICAL_AREAS.filter((area) => value.includes(area)),
                }))
              }
            />
            <TopicInterestPicker
              value={extras.topics || []}
              onChange={(topics) => setExtras((current) => ({ ...current, topics }))}
            />
          </div>
        </Card>

        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Account</h2>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-700">Access level</p>
              <p className="text-sm text-gray-600 mt-1">{getAccessLevelDisplayName(accessLevel)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Connected accounts</p>
              {accounts.length === 0 ? (
                <p className="text-sm text-gray-500">No social accounts connected.</p>
              ) : (
                <ul className="space-y-2">
                  {accounts.map((account) => (
                    <li key={account.id} className="flex items-center gap-2 text-sm text-gray-600">
                      <LinkIcon className="h-4 w-4 text-gray-400" aria-hidden />
                      {connectedAccountLabel(account.provider)}
                      {account.emailAddress ? ` · ${account.emailAddress}` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Card>

        <Button type="submit" className="min-h-[44px]" disabled={saving}>
          {saving ? 'Saving…' : 'Save profile'}
        </Button>
      </form>

      <div className="mt-10">
        <AccountPreferences hospitalOptions={hospitalOptions} />
      </div>
      <AccountReverificationModal
        isOpen={Boolean(reverification)}
        level={reverification?.level}
        purpose={reverification?.purpose}
        preferredEmailAddressId={user.primaryEmailAddressId}
        onComplete={() => reverification?.onComplete()}
        onCancel={() => reverification?.onCancel()}
      />
    </div>
  );
}
